import { useState, useEffect, type ReactNode } from 'react';
import { Avatar, I } from '../atoms';
import { usePlanningPermissions } from '../usePlanningPermissions';
import { getUserEmail, logout } from '../auth';
import { CONFIG } from '../config';
import { usePwa, showIosInstallHint, isStandalone, IosInstallSteps } from '../pwa';

// ─── Navigation structure ──────────────────────────────────────────────────────

const NAV_GROUPS: { group: string; items: { id: string; label: string; permission: string }[] }[] = [
  {
    group: 'Planning',
    items: [
      { id: 'assess',        label: 'Assess',         permission: 'planning-cases:write' },
      { id: 'cases',         label: 'Cases',          permission: 'planning-cases:read' },
      { id: 'policy-inputs', label: 'Policy Inputs',  permission: 'policy-inputs:read' },
    ],
  },
  {
    group: 'Administration',
    items: [
      { id: 'users',    label: 'Users',    permission: 'users:read' },
      { id: 'settings', label: 'Settings', permission: 'planning-settings:read' },
    ],
  },
];

function navHref(id: string): string {
  return `#/${id}`;
}

// ─── Types ─────────────────────────────────────────────────────────────────────

interface ShellProps {
  current: string;
  crumbs: { label: string; href?: string }[];
  children: ReactNode;
  density?: string;
  theme?: string;
  accent?: string;
  monoNumerals?: boolean;
}

// ─── Sidebar ───────────────────────────────────────────────────────────────────

function Sidebar({ active, mobileOpen, onClose }: {
  active: string; mobileOpen: boolean; onClose: () => void;
}) {
  const { can } = usePlanningPermissions();
  const groups = NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) => can(i.permission)) }))
    .filter((g) => g.items.length > 0);
  return (
    <div style={{ display: 'contents' }}>
      {mobileOpen && <div className="sb-scrim" onClick={onClose} />}
      <aside className={`sidebar ${mobileOpen ? 'sidebar-open' : ''}`}>
        <div className="sb-brand">
          <img className="sb-brand-logo" src="./assets/elw-logo.png" alt="ELW" />
          <div className="sb-brand-text">
            <span className="sb-brand-name">Elevations Planning</span>
            <span className="sb-brand-by">by ELW</span>
          </div>
          {CONFIG.environment && <span className="sb-brand-env">{CONFIG.environment}</span>}
          <button className="sb-close" onClick={onClose} aria-label="Close menu">&times;</button>
        </div>
        <nav className="sb-nav">
          {groups.map((group) => (
            <div key={group.group}>
              <div className="sb-group-label">{group.group}</div>
              {group.items.map((item) => (
                <a
                  key={item.id}
                  className={`sb-item ${item.id === active ? 'active' : ''}`}
                  href={navHref(item.id)}
                >
                  <span>{item.label}</span>
                </a>
              ))}
            </div>
          ))}
        </nav>
        <div className="sb-foot">
          <Avatar initials={getUserEmail()?.slice(0, 2).toUpperCase() ?? 'U'} size={28} color="oklch(54% 0.16 245)" />
          <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
            <div className="role" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getUserEmail() ?? 'User'}</div>
            <small><a href="#/account" style={{ color: 'inherit' }}>Your account</a></small>
          </div>
          <button
            onClick={() => logout(CONFIG.cognitoDomain, CONFIG.clientId)}
            title="Sign out"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', padding: '4px', flexShrink: 0, lineHeight: 1 }}
            aria-label="Sign out"
          >
            <svg viewBox="0 0 16 16" fill="none" width="14" height="14">
              <path d="M6 2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3M10 11l3-3-3-3M13 8H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </aside>
    </div>
  );
}

function InstallButton({ installPrompt, onInstall }: { installPrompt: BeforeInstallPromptEvent | null; onInstall: () => void }) {
  const [open, setOpen] = useState(false);
  const icon = (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="2" x2="8" y2="10" />
      <polyline points="4,7 8,11 12,7" />
      <line x1="2" y1="13" x2="14" y2="13" />
    </svg>
  );

  if (installPrompt) {
    return (
      <button className="tb-action tb-icon" onClick={onInstall} title="Install app" aria-label="Install app">
        {icon}
      </button>
    );
  }

  if (showIosInstallHint) {
    return (
      <div style={{ position: 'relative' }}>
        <button className="tb-action tb-icon" onClick={() => setOpen(o => !o)} title="Install app" aria-label="Install app">
          {icon}
        </button>
        {open && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={() => setOpen(false)} />
            <div style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 1000,
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 10, padding: '14px 16px', width: 240,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)', fontSize: 13, lineHeight: 1.5,
            }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Install Elevations Planning</div>
              <div style={{ color: 'var(--ink-2)' }}><IosInstallSteps /></div>
            </div>
          </>
        )}
      </div>
    );
  }

  return null;
}

// ── Topbar ────────────────────────────────────────────────────────────────────

function Topbar({ crumbs, onOpenNav, onToggleTheme, theme, installPrompt, onInstall }: {
  crumbs: { label: string; href?: string }[];
  onOpenNav: () => void;
  onToggleTheme: () => void;
  theme: string;
  installPrompt: BeforeInstallPromptEvent | null;
  onInstall: () => void;
}) {
  return (
    <div className="topbar">
      <div className="topbar-inner">
        <button className="tb-action tb-nav-toggle" onClick={onOpenNav} aria-label="Open menu">
          <svg viewBox="0 0 16 16" fill="none" width="14" height="14">
            <path d="M2.5 4h11M2.5 8h11M2.5 12h11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        </button>
        <div className="tb-brand">
          <img src="./assets/elw-logo.png" alt="ELW" style={{ height: 22 }} />
          <span className="tb-brand-name">Elevations Planning</span>
          {CONFIG.environment && <span className="sb-brand-env">{CONFIG.environment}</span>}
        </div>
        {isStandalone && (
          <>
            <button className="tb-action tb-icon" onClick={() => window.history.back()} aria-label="Back">
              <svg viewBox="0 0 16 16" fill="none" width="14" height="14">
                <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button className="tb-action tb-icon" onClick={() => window.history.forward()} aria-label="Forward">
              <svg viewBox="0 0 16 16" fill="none" width="14" height="14">
                <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button className="tb-action tb-icon" onClick={() => window.location.reload()} aria-label="Refresh">
              <svg viewBox="0 0 16 16" fill="none" width="14" height="14">
                <path d="M13.5 8A5.5 5.5 0 1 1 10 3.07" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                <polyline points="10,1 10,4 13,4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </>
        )}
        <div className="crumb">
          {crumbs.map((c, i) => (
            <span key={i} style={{ display: 'contents' }}>
              {i > 0 && <span className="sep">{I.chev}</span>}
              {c.href
                ? <a href={c.href}>{c.label}</a>
                : <span className={i === crumbs.length - 1 ? 'cur' : ''}>{c.label}</span>}
            </span>
          ))}
        </div>
        <div className="tb-spacer" />
        <button className="tb-action tb-icon" onClick={onToggleTheme} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} aria-label="Toggle theme">
          {theme === 'dark' ? (
            <svg viewBox="0 0 16 16" fill="none" width="15" height="15">
              <circle cx="8" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M8 1v1.5M8 13.5V15M1 8h1.5M13.5 8H15M3.05 3.05l1.06 1.06M11.89 11.89l1.06 1.06M3.05 12.95l1.06-1.06M11.89 4.11l1.06-1.06" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          ) : (
            <svg viewBox="0 0 16 16" fill="none" width="15" height="15">
              <path d="M13.5 9.5A6 6 0 0 1 6.5 2.5a6 6 0 1 0 7 7z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
            </svg>
          )}
        </button>
        <InstallButton installPrompt={installPrompt} onInstall={onInstall} />
      </div>
    </div>
  );
}

// ─── Shell ─────────────────────────────────────────────────────────────────────

const THEME_KEY = 'ep:theme';

export function Shell({
  current, crumbs, children,
  density = 'comfortable', theme: themeProp = 'light', accent = '#1e3a8a',
  monoNumerals = true,
}: ShellProps) {
  const { installPrompt, handleInstall } = usePwa();
  const [navOpen, setNavOpen] = useState(false);
  const [theme, setTheme] = useState<string>(() => localStorage.getItem(THEME_KEY) ?? themeProp);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem(THEME_KEY, next);
    document.documentElement.dataset.theme = next;
  };

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    document.documentElement.style.setProperty('--accent', accent);
  }, [accent]);

  return (
    <div
      className="shell"
      data-density={density}
      data-theme={theme}
      data-mono={monoNumerals ? 'true' : 'false'}
    >
      <Sidebar active={current} mobileOpen={navOpen} onClose={() => setNavOpen(false)} />
      <div className="main">
        <Topbar
          crumbs={crumbs}
          onOpenNav={() => setNavOpen(true)}
          onToggleTheme={toggleTheme}
          theme={theme}
          installPrompt={installPrompt}
          onInstall={handleInstall}
        />
        <div className="scroll">
          <div className="page">{children}</div>
        </div>
      </div>
    </div>
  );
}
